/**
 * Painel da PEÇA DESMEMBRADA: o que ela é, de qual ambiente saiu, por onde
 * está andando, o status com as ações que ele aceita ("voltou para o
 * ambiente", "concluir sem retornar"), prazos, tarefas, comentários e a linha
 * do tempo dela.
 *
 * A peça é derivada do ambiente: prazo e prioridade podem ser dela ou
 * herdados, e a tela SEMPRE diz qual é o caso (ADR-0059 — rótulo tem que
 * dizer a régua).
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiPlus } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { listarEventosPeca } from "../../../services/mapaProducao";
import { formatarDataLocal } from "../../../utils/dinheiro";
import { montarTimelineAmbiente } from "../dados/timeline";
import {
  ROTULO_PRIORIDADE,
  ROTULO_STATUS_PECA,
  ROTULO_STATUS_TAREFA,
  TOM_PRIORIDADE,
  TOM_STATUS_PECA,
  TOM_STATUS_TAREFA,
  acoesDaPeca,
  pecaFora,
} from "../dados/vocabulario";
import { CampoColaborador, CampoPrioridade, CamposLugar, limparEspacos } from "./formularios";
import { pessoasDaPeca, textoDaOrigem, textoDoPrazo } from "./modelo";
import { Acoes, Ajuda, Campo, Campos, Dados, ItemLista, Lista, Secao, Selos, Vazio } from "./styles";
import { ListaTimeline } from "./Timeline";
import { ErroDaAcao, PillStatus, useErroDeAcao } from "./ui";

const TAMANHO_NOME = 160;
const TAMANHO_COMENTARIO = 4000;

const SUCESSO_DA_ACAO = {
  iniciar: "Peça iniciada.",
  pausar: "Peça pausada.",
  retomar: "Peça retomada.",
  aguardar: "Peça voltou para a fila.",
  retornar: "Peça voltou para o ambiente.",
  concluir: "Peça concluída sem retorno.",
  cancelar: "Desmembramento cancelado.",
  reabrir: "Peça reaberta.",
};

const quandoOuTraco = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "Ainda não";
const diaOuTraco = (valor) => (valor ? formatarDataLocal(valor) : "Sem data");

const SEM_HISTORICO = { eventos: [], erro: null };

/**
 * Histórico da peça. O guardado anda SEMPRE junto do id de quem foi lido:
 * abrir outra peça não pode mostrar os eventos da anterior enquanto a leitura
 * nova não chega. Reler a MESMA peça (`gatilho`) mantém a lista à vista.
 */
function useEventosDaPeca(pecaId, aberto, gatilho) {
  const { request } = useAuth();
  const [lido, setLido] = useState({ pecaId: null, ...SEM_HISTORICO });
  useEffect(() => {
    if (!aberto || !pecaId) return undefined;
    let vivo = true;
    Promise.resolve()
      .then(() => listarEventosPeca(request, pecaId))
      .then((lista) => {
        if (vivo) setLido({ pecaId, eventos: Array.isArray(lista) ? lista : [], erro: null });
      })
      .catch((e) => {
        if (!vivo) return;
        const erro = e?.message || "Não foi possível ler o histórico.";
        setLido((atual) => ({ pecaId, eventos: atual.pecaId === pecaId ? atual.eventos : [], erro }));
      });
    return () => {
      vivo = false;
    };
  }, [request, pecaId, aberto, gatilho]);
  return pecaId && lido.pecaId === pecaId ? { eventos: lido.eventos, erro: lido.erro } : SEM_HISTORICO;
}

function Resumo({ peca, indice, hoje, onAbrirAmbiente }) {
  const prazo = textoDoPrazo(peca.prazoEfetivo, hoje);
  const pessoas = pessoasDaPeca(peca, indice);
  const reguaPrazo = textoDaOrigem(peca.origemPrazo);
  const reguaPrioridade = textoDaOrigem(peca.origemPrioridade, { feminino: true });
  return (
    <Secao aria-label="Resumo da peça">
      <Selos>
        <PillStatus tom={TOM_STATUS_PECA[peca.status]}>{ROTULO_STATUS_PECA[peca.status]}</PillStatus>
        {peca.atrasada && <PillStatus tom="danger">{prazo.texto}</PillStatus>}
        <PillStatus tom={TOM_PRIORIDADE[peca.prioridade]} ponto={false} title={reguaPrioridade}>
          {ROTULO_PRIORIDADE[peca.prioridade]}
          {reguaPrioridade ? ` · ${reguaPrioridade}` : ""}
        </PillStatus>
      </Selos>
      {peca.status === "pausada" && peca.motivo_pausa && <p className="nota">Pausada: {peca.motivo_pausa}</p>}
      {peca.status === "cancelada" && peca.motivo_cancelamento && (
        <p className="nota">Cancelada: {peca.motivo_cancelamento}</p>
      )}
      {!pecaFora(peca) && (
        <p className="nota">
          Esta peça já saiu de circulação: {ROTULO_STATUS_PECA[peca.status].toLowerCase()}. Reabra se ela precisar
          andar de novo.
        </p>
      )}
      <Dados>
        <div>
          <dt>Ambiente de origem</dt>
          <dd>
            {peca.ambienteRotulo ? (
              onAbrirAmbiente ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  style={{ padding: 0, height: "auto" }}
                  onClick={() => onAbrirAmbiente(peca.ambienteChave)}
                >
                  {peca.ambienteRotulo}
                </button>
              ) : (
                peca.ambienteRotulo
              )
            ) : (
              "Não carregado nesta planilha"
            )}
          </dd>
        </div>
        <div>
          <dt>Cliente</dt>
          <dd>{peca.cliente || "Sem cliente"}</dd>
        </div>
        <div>
          <dt>Onde está</dt>
          <dd>
            {[
              peca.setorNome,
              indice.subsetores.get(peca.subsetorId)?.nome,
              indice.postos.get(peca.postoId)?.nome,
            ]
              .filter(Boolean)
              .join(" › ") || "Sem lugar apontado"}
          </dd>
        </div>
        <div>
          <dt>Prazo que vale</dt>
          <dd>
            {diaOuTraco(peca.prazoEfetivo)}
            {reguaPrazo ? ` (${reguaPrazo})` : ""}
          </dd>
        </div>
        <div>
          <dt>Previsão</dt>
          <dd>{diaOuTraco(peca.prevista)}</dd>
        </div>
        <div>
          <dt>Responsáveis</dt>
          <dd>{pessoas.length ? pessoas.map((p) => p.nome).join(", ") : "Ninguém apontado"}</dd>
        </div>
        <div>
          <dt>Desmembrada</dt>
          <dd>
            {quandoOuTraco(peca.criado_em)}
            {peca.criado_por ? ` por ${peca.criado_por}` : ""}
          </dd>
        </div>
        <div>
          <dt>Voltou ao ambiente</dt>
          <dd>{quandoOuTraco(peca.retornado_em)}</dd>
        </div>
        <div>
          <dt>Concluída</dt>
          <dd>{quandoOuTraco(peca.concluido_em)}</dd>
        </div>
      </Dados>
    </Secao>
  );
}

function AcoesDaPeca({ peca, podeOperar, podeGerir, acoes, rodar }) {
  const [pendente, setPendente] = useState(null);
  const [motivo, setMotivo] = useState("");
  const disponiveis = acoesDaPeca(peca.status, { podeOperar, podeGerir });
  useEffect(() => {
    setPendente(null);
    setMotivo("");
  }, [peca.status]);
  if (!disponiveis.length) return null;

  const executar = (acao, texto = "") => rodar(() => acoes.acaoPeca(peca, acao.acao, texto));

  if (pendente) {
    const faltaMotivo = pendente.exigeMotivo && !motivo.trim();
    return (
      <Secao aria-label="Ações da peça">
        <h3>{pendente.rotulo}</h3>
        <Campo>
          {pendente.exigeMotivo ? "Motivo (obrigatório)" : "Motivo (opcional)"}
          <textarea value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} />
        </Campo>
        <Acoes>
          <AsyncButton
            variant={pendente.perigosa ? "danger" : "primary"}
            silentError
            disabled={faltaMotivo}
            successMessage={SUCESSO_DA_ACAO[pendente.acao]}
            onClick={() => executar(pendente, motivo.trim()).then(() => setPendente(null))}
          >
            Confirmar: {pendente.rotulo.toLowerCase()}
          </AsyncButton>
          <button type="button" className="btn btn--secondary" onClick={() => setPendente(null)}>
            Voltar
          </button>
        </Acoes>
      </Secao>
    );
  }

  return (
    <Secao aria-label="Ações da peça">
      <h3>Ações</h3>
      <Acoes>
        {disponiveis.map((acao) =>
          acao.pedeMotivo ? (
            <button
              key={acao.acao}
              type="button"
              className={acao.perigosa ? "btn btn--ghost" : "btn btn--secondary"}
              style={acao.perigosa ? { color: "var(--danger-text)" } : undefined}
              onClick={() => setPendente(acao)}
            >
              {acao.rotulo}
            </button>
          ) : (
            <AsyncButton
              key={acao.acao}
              variant={acao.acao === "iniciar" || acao.acao === "retornar" ? "primary" : "secondary"}
              silentError
              successMessage={SUCESSO_DA_ACAO[acao.acao]}
              onClick={() => executar(acao)}
            >
              {acao.rotulo}
            </AsyncButton>
          )
        )}
      </Acoes>
    </Secao>
  );
}

function valoresDaPeca(peca) {
  return {
    nome: peca.nome || "",
    colaboradorId: peca.colaboradorId || null,
    prioridade: peca.prioridade,
    prevista: peca.data_prevista || "",
    limite: peca.data_limite || "",
    motivo: "",
  };
}

// Só o que mudou vai no PATCH (o backend registra um evento por mudança).
function camposAlterados(peca, form) {
  const antes = valoresDaPeca(peca);
  const mapa = {
    nome: ["nome", limparEspacos(form.nome)],
    colaboradorId: ["colaborador_id", form.colaboradorId || null],
    prioridade: ["prioridade", form.prioridade],
    prevista: ["data_prevista", form.prevista || null],
    limite: ["data_limite", form.limite || null],
  };
  const campos = {};
  Object.entries(mapa).forEach(([chave, [campo, valor]]) => {
    const anterior = chave === "nome" ? antes.nome.trim() : antes[chave] || null;
    if ((valor || null) !== (anterior || null)) campos[campo] = valor;
  });
  return campos;
}

function EditarPeca({ peca, estado, podeEditar, acoes, rodar }) {
  const [form, setForm] = useState(() => valoresDaPeca(peca));
  const versao = `${peca.id}:${peca.versao}`;
  useEffect(() => {
    setForm(valoresDaPeca(peca));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versao]);
  const campos = camposAlterados(peca, form);
  const mudouPrazo = "data_prevista" in campos || "data_limite" in campos;
  const semNome = !limparEspacos(form.nome);
  const mudar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  return (
    <Secao aria-label="Dados da peça">
      <h3>Dados da peça</h3>
      <Campo>
        Nome
        <input
          value={form.nome}
          maxLength={TAMANHO_NOME}
          onChange={mudar("nome")}
          onBlur={() => setForm((f) => ({ ...f, nome: limparEspacos(f.nome) }))}
          disabled={!podeEditar}
        />
      </Campo>
      <CampoColaborador
        setorId={peca.setorId}
        colaboradores={estado?.colaboradores}
        valor={form.colaboradorId}
        onChange={(colaboradorId) => setForm((f) => ({ ...f, colaboradorId }))}
        desabilitado={!podeEditar}
        rotulo="Responsável"
      />
      <CampoPrioridade
        valor={form.prioridade}
        onChange={(prioridade) => setForm((f) => ({ ...f, prioridade: prioridade || f.prioridade }))}
        desabilitado={!podeEditar}
        ajuda={
          peca.origemPrioridade === "peca"
            ? "Escolhida nesta peça."
            : `Hoje ${textoDaOrigem(peca.origemPrioridade, { feminino: true })}: salvar aqui passa a valer só para a peça.`
        }
      />
      <Campos $min={160}>
        <Campo>
          Previsão
          <input type="date" value={form.prevista} onChange={mudar("prevista")} disabled={!podeEditar} />
        </Campo>
        <Campo>
          Data limite
          <input type="date" value={form.limite} onChange={mudar("limite")} disabled={!podeEditar} />
        </Campo>
      </Campos>
      <Ajuda>Em branco, vale o prazo do ambiente de origem ({diaOuTraco(peca.ambiente?.prazoEfetivo)}).</Ajuda>
      {podeEditar && mudouPrazo && (
        <Campo>
          Motivo da mudança de prazo (opcional)
          <textarea value={form.motivo} maxLength={500} onChange={mudar("motivo")} />
        </Campo>
      )}
      {podeEditar && (
        <Acoes>
          <AsyncButton
            silentError
            disabled={semNome || Object.keys(campos).length === 0}
            successMessage="Peça atualizada."
            onClick={() => rodar(() => acoes.salvarPeca(peca, campos, form.motivo.trim()))}
          >
            Salvar alterações
          </AsyncButton>
          {semNome && <span style={{ fontSize: 12, color: "var(--danger-text)" }}>O nome não pode ficar vazio.</span>}
        </Acoes>
      )}
    </Secao>
  );
}

function MoverPeca({ peca, catalogo, estado, podeOperar, acoes, rodar }) {
  const inicial = () => ({ setorId: peca.setorId, subsetorId: peca.subsetorId, postoId: peca.postoId });
  const [lugar, setLugar] = useState(inicial);
  const versao = `${peca.id}:${peca.versao}`;
  useEffect(() => {
    setLugar(inicial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versao]);
  const mudou =
    lugar.setorId !== peca.setorId ||
    (lugar.subsetorId || null) !== (peca.subsetorId || null) ||
    (lugar.postoId || null) !== (peca.postoId || null);
  // Trocar de setor invalida subsetor e posto: eles pertencem ao setor antigo.
  const trocarSetor = (setorId) => setLugar({ setorId, subsetorId: null, postoId: null });
  // O setor onde a peça está entra na lista mesmo desativado: sem ele o select
  // ficaria vazio e um toque em Salvar moveria a peça sem ninguém pedir.
  const setoresPossiveis = catalogo.todos.filter((s) => s.ativo !== false || s.id === lugar.setorId);

  return (
    <Secao aria-label="Mover a peça">
      <h3>Onde a peça está</h3>
      <Campo>
        Setor
        <select value={lugar.setorId || ""} onChange={(e) => trocarSetor(e.target.value)} disabled={!podeOperar}>
          {setoresPossiveis.map((s) => (
            <option key={s.id} value={s.id}>
              {s.nome}
              {s.ativo === false ? " (desativado)" : ""}
            </option>
          ))}
        </select>
      </Campo>
      <CamposLugar
        setorId={lugar.setorId}
        estado={estado}
        subsetorId={lugar.subsetorId}
        postoId={lugar.postoId}
        onChange={(novo) => setLugar((l) => ({ ...l, ...novo }))}
        desabilitado={!podeOperar}
      />
      {podeOperar && (
        <Acoes>
          <AsyncButton
            silentError
            disabled={!mudou}
            successMessage="Peça movida."
            onClick={() => rodar(() => acoes.moverPeca(peca, lugar))}
          >
            Salvar lugar
          </AsyncButton>
        </Acoes>
      )}
    </Secao>
  );
}

function TarefasDaPeca({ tarefas, indice, podeCriar, onAbrirTarefa, onNovaTarefa }) {
  return (
    <Secao aria-label="Tarefas da peça">
      <h3>Tarefas</h3>
      {tarefas.length === 0 ? (
        <p className="nota">Nenhuma tarefa ligada a esta peça.</p>
      ) : (
        <Lista>
          {tarefas.map((t) => (
            <ItemLista key={t.id} data-clicavel="sim" onClick={() => onAbrirTarefa(t)}>
              <span className="principal">
                <span className="l1">{t.codigo} · {t.titulo}</span>
                <span className="l2">
                  {indice.colaboradores.get(t.colaborador_id)?.nome || "Sem colaborador"}
                  {t.data_limite ? ` · limite ${formatarDataLocal(t.data_limite)}` : ""}
                </span>
              </span>
              <PillStatus tom={TOM_STATUS_TAREFA[t.status]}>{ROTULO_STATUS_TAREFA[t.status]}</PillStatus>
            </ItemLista>
          ))}
        </Lista>
      )}
      {podeCriar && (
        <Acoes>
          <button type="button" className="btn btn--secondary" onClick={onNovaTarefa}>
            <FiPlus size={14} aria-hidden="true" /> Nova tarefa para esta peça
          </button>
        </Acoes>
      )}
    </Secao>
  );
}

function ComentarPeca({ peca, acoes, rodar, onComentou }) {
  const [texto, setTexto] = useState("");
  return (
    <Secao aria-label="Comentar na peça">
      <h3>Comentário</h3>
      <Campo>
        <textarea
          value={texto}
          maxLength={TAMANHO_COMENTARIO}
          onChange={(e) => setTexto(e.target.value)}
          aria-label="Comentário da peça"
        />
      </Campo>
      <Acoes>
        <AsyncButton
          variant="secondary"
          silentError
          disabled={!texto.trim()}
          successMessage="Comentário registrado."
          onClick={() =>
            rodar(async () => {
              await acoes.comentarPeca(peca, texto.trim());
              setTexto("");
              onComentou();
            })
          }
        >
          Comentar
        </AsyncButton>
      </Acoes>
    </Secao>
  );
}

/**
 * Props: aberto, peca (combinada, atual) ou null, indice, estado, catalogo,
 * tarefasDaPeca, podeOperar, podeGerir, hoje, acoes, onFechar,
 * onAbrirTarefa, onNovaTarefa(peca), onAbrirAmbiente(chave), onRecarregar.
 */
export default function DetalhePeca({
  aberto,
  peca,
  indice,
  estado,
  catalogo,
  tarefasDaPeca = [],
  podeOperar,
  podeGerir,
  hoje,
  acoes,
  onFechar,
  onAbrirTarefa,
  onNovaTarefa,
  onAbrirAmbiente,
  onRecarregar,
}) {
  const { erro, rodar, limpar } = useErroDeAcao();
  const [gatilho, setGatilho] = useState(0);
  const { eventos, erro: erroHistorico } = useEventosDaPeca(peca?.id, aberto, `${peca?.versao}:${gatilho}`);
  const timeline = useMemo(
    () => (peca ? montarTimelineAmbiente(null, eventos, tarefasDaPeca, [peca]) : []),
    [peca, eventos, tarefasDaPeca]
  );
  useEffect(() => {
    if (!aberto) limpar();
  }, [aberto, limpar]);

  const emCirculacao = peca ? pecaFora(peca) : false;
  const titulo = peca ? peca.rotulo || peca.nome : "Peça desmembrada";
  const subtitulo = peca
    ? [ROTULO_STATUS_PECA[peca.status], peca.setorNome, peca.ambienteRotulo].filter(Boolean).join(" · ")
    : "";

  return (
    <SlideOverPanel open={aberto} onClose={onFechar} title={titulo} subtitle={subtitulo} width="min(96vw, 620px)">
      {!peca ? (
        <Vazio>Esta peça não está mais na lista (ela pode ter voltado ao ambiente ou sido concluída).</Vazio>
      ) : (
        <>
          <ErroDaAcao erro={erro} onRecarregar={onRecarregar} />
          <Resumo peca={peca} indice={indice} hoje={hoje} onAbrirAmbiente={onAbrirAmbiente} />
          <AcoesDaPeca peca={peca} podeOperar={podeOperar} podeGerir={podeGerir} acoes={acoes} rodar={rodar} />
          <EditarPeca
            peca={peca}
            estado={estado}
            podeEditar={podeOperar && emCirculacao}
            acoes={acoes}
            rodar={rodar}
          />
          {emCirculacao && catalogo && (
            <MoverPeca
              peca={peca}
              catalogo={catalogo}
              estado={estado}
              podeOperar={podeOperar}
              acoes={acoes}
              rodar={rodar}
            />
          )}
          <TarefasDaPeca
            tarefas={tarefasDaPeca}
            indice={indice}
            podeCriar={podeOperar && emCirculacao}
            onAbrirTarefa={onAbrirTarefa}
            onNovaTarefa={() => onNovaTarefa(peca)}
          />
          {podeOperar && (
            <ComentarPeca peca={peca} acoes={acoes} rodar={rodar} onComentou={() => setGatilho((n) => n + 1)} />
          )}
          <Secao aria-label="Linha do tempo da peça">
            <h3>Linha do tempo</h3>
            {erroHistorico && <p className="nota">{erroHistorico}</p>}
            <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
              <ListaTimeline itens={timeline} compacto hoje={hoje} vazio="Sem registros ainda." />
            </div>
          </Secao>
        </>
      )}
    </SlideOverPanel>
  );
}
